import React from "react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/shared/components/ui/dropdown-menu";
import {
  ArrowDownToLine, ArrowUpFromLine, MoreHorizontal, Eye, RefreshCw,
  XCircle, Copy, CheckCircle2, Clock, Loader2, AlertTriangle,
} from "lucide-react";

/* ── Unified Design Tokens ──────────────────────────────────────── */
const statusConfigDefault = {
  success: { icon: CheckCircle2, darkColor: "rgba(16,185,129,0.12)", darkBorder: "rgba(16,185,129,0.28)", darkText: "#34D399", label: "Success" },
  pending: { icon: Clock, darkColor: "rgba(251,191,36,0.12)", darkBorder: "rgba(251,191,36,0.28)", darkText: "#FBBF24", label: "Pending" },
  processing: { icon: Loader2, darkColor: "rgba(59,130,246,0.12)", darkBorder: "rgba(59,130,246,0.28)", darkText: "#60A5FA", label: "Processing" },
  failed: { icon: XCircle, darkColor: "rgba(251,113,133,0.12)", darkBorder: "rgba(251,113,133,0.28)", darkText: "#FB7185", label: "Failed" },
  expired: { icon: AlertTriangle, darkColor: "rgba(71,85,105,0.18)", darkBorder: "rgba(71,85,105,0.30)", darkText: "#64748B", label: "Expired" },
  cancelled: { icon: XCircle, darkColor: "rgba(71,85,105,0.18)", darkBorder: "rgba(71,85,105,0.30)", darkText: "#64748B", label: "Cancelled" },
  declined: { icon: XCircle, darkColor: "rgba(251,113,133,0.12)", darkBorder: "rgba(251,113,133,0.28)", darkText: "#FB7185", label: "Declined" },
};

const getStatusConfig = (t) => {
  if (!t) return statusConfigDefault;
  return {
    success: { ...statusConfigDefault.success, label: t("success") },
    pending: { ...statusConfigDefault.pending, label: t("pending") },
    processing: { ...statusConfigDefault.processing, label: t("processing") },
    failed: { ...statusConfigDefault.failed, label: t("failed") },
    expired: { ...statusConfigDefault.expired, label: t("expired") },
    cancelled: { ...statusConfigDefault.cancelled, label: t("cancelled") },
    declined: { ...statusConfigDefault.declined, label: t("declined") || "Declined" },
  };
};

/* ── Scoped CSS ─────────────────────────────────────────────────── */
const TT_CSS = `
  .tt-root {
    font-family: 'Outfit', system-ui, sans-serif;
    background: linear-gradient(155deg, #122040, #0E1C35);
    border: 1px solid rgba(255,255,255,0.055);
    border-radius: 1rem; overflow: hidden; transition: border-color .25s, box-shadow .25s;
  }
  .tt-root:hover { border-color: rgba(34,211,238,0.16); box-shadow: 0 4px 24px rgba(0,0,0,0.25); }

  .tt-scroll-wrap { overflow-x: auto; }
  .tt-scroll-wrap::-webkit-scrollbar { height: 5px; }
  .tt-scroll-wrap::-webkit-scrollbar-track { background: transparent; }
  .tt-scroll-wrap::-webkit-scrollbar-thumb { background: rgba(34,211,238,0.18); border-radius: 99px; }
  .tt-scroll-wrap::-webkit-scrollbar-thumb:hover { background: rgba(34,211,238,0.35); }

  .tt-table { width: 100%; min-width: 800px; border-collapse: collapse; font-family: 'Outfit', system-ui, sans-serif; }

  .tt-head-row { background: rgba(255,255,255,0.025); border-bottom: 1px solid rgba(255,255,255,0.06); }
  .tt-th {
    padding: 11px 16px; font-size: 10px; font-weight: 800; color: #3E5E7E;
    text-transform: uppercase; letter-spacing: .14em; white-space: nowrap; text-align: left;
  }
  .tt-th--right { text-align: right !important; }
  .tt-th--action { width: 3rem; }

  .tt-body-row { border-bottom: 1px solid rgba(255,255,255,0.04); transition: background .16s; cursor: pointer; }
  .tt-body-row:last-child { border-bottom: none; }
  .tt-body-row:hover { background: rgba(34,211,238,0.045); }
  .tt-body-row:hover .tt-first-td { border-left: 3px solid #22D3EE; }

  .tt-td { padding: 13px 16px; font-size: 13px; font-weight: 500; vertical-align: middle; color: #93B4D4; transition: color .16s; }
  .tt-body-row:hover .tt-td { color: #EFF6FF; }

  .tt-type-icon {
    width: 2rem; height: 2rem; border-radius: .5rem;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    border: 1px solid transparent; transition: box-shadow .2s, border-color .2s;
  }
  .tt-type-icon svg { width: .9rem; height: .9rem; }
  .tt-type-icon--dep { background: rgba(59,130,246,0.10); border-color: rgba(59,130,246,0.18); }
  .tt-type-icon--dep svg { color: #60A5FA; }
  .tt-type-icon--wd  { background: rgba(251,191,36,0.10); border-color: rgba(251,191,36,0.18); }
  .tt-type-icon--wd svg  { color: #FBBF24; }
  .tt-body-row:hover .tt-type-icon--dep { border-color: rgba(59,130,246,0.4); box-shadow: 0 0 8px rgba(59,130,246,0.15); }
  .tt-body-row:hover .tt-type-icon--wd  { border-color: rgba(251,191,36,0.4);  box-shadow: 0 0 8px rgba(251,191,36,0.15); }

  .tt-id-cell { display: flex; align-items: center; gap: .625rem; }
  .tt-id-row  { display: flex; align-items: center; gap: .35rem; }
  .tt-id-mono { font-family: ui-monospace, monospace; font-size: .75rem; color: #93B4D4; transition: color .16s; }
  .tt-body-row:hover .tt-id-mono { color: #22D3EE; }
  .tt-sub-text { display: block; font-size: .7rem; color: #4D6E90; margin-top: .1rem; }

  .tt-copy-btn {
    background: none; border: none; cursor: pointer; padding: .125rem; border-radius: .25rem;
    display: flex; align-items: center; opacity: 0; transition: opacity .16s, background .16s;
  }
  .tt-body-row:hover .tt-copy-btn { opacity: 1; }
  .tt-copy-btn:hover { background: rgba(34,211,238,0.12); }
  .tt-copy-btn svg { width: .7rem; height: .7rem; color: #4D6E90; transition: color .16s; }
  .tt-copy-btn:hover svg { color: #22D3EE; }

  .tt-badge-type {
    display: inline-block; padding: .15rem .45rem; border-radius: .4rem;
    font-size: .68rem; font-weight: 700;
    background: rgba(34,211,238,0.10); border: 1px solid rgba(34,211,238,0.22); color: #22D3EE;
  }

  .tt-reseller-name { font-weight: 600; font-size: .8125rem; color: #B8D4EE; }
  .tt-direct-label  { font-size: .75rem; color: #4D6E90; }
  .tt-merchant-name { font-weight: 600; font-size: .8375rem; color: #B8D4EE; }

  .tt-provider-tag {
    display: inline-block; padding: .2rem .5rem; border-radius: .375rem;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07);
    font-size: .75rem; color: #93B4D4; transition: border-color .16s, color .16s;
  }
  .tt-body-row:hover .tt-provider-tag { border-color: rgba(34,211,238,0.22); color: #EFF6FF; }

  .tt-amount-dep { font-weight: 700; color: #60A5FA; }
  .tt-amount-wd  { font-weight: 700; color: #FBBF24; }

  .tt-status-badge {
    display: inline-flex; align-items: center; gap: .3rem; padding: .2rem .55rem;
    border-radius: 9999px; font-size: .68rem; font-weight: 700; border: 1px solid transparent;
    letter-spacing: .02em;
  }
  .tt-status-icon { width: .7rem; height: .7rem; flex-shrink: 0; }

  .tt-date { display: flex; flex-direction: column; gap: .1rem; }
  .tt-date-main { font-size: .8rem; color: #93B4D4; }
  .tt-date-time { font-size: .7rem; color: #4D6E90; font-family: ui-monospace, monospace; }
  .tt-body-row:hover .tt-date-main { color: #EFF6FF; }

  .tt-action-btn {
    width: 1.75rem; height: 1.75rem; border-radius: .45rem;
    border: 1px solid rgba(34,211,238,0.18); background: rgba(34,211,238,0.07);
    display: flex; align-items: center; justify-content: center; cursor: pointer;
    transition: background .18s, border-color .18s, box-shadow .18s;
  }
  .tt-action-btn svg { width: .85rem; height: .85rem; color: #22D3EE; }
  .tt-action-btn:hover { background: rgba(34,211,238,0.14); border-color: rgba(34,211,238,0.38); box-shadow: 0 0 12px rgba(34,211,238,0.18); }

  /* Dropdown */
  .tt-dropdown-content {
    background: #0E1C35 !important; border: 1px solid rgba(255,255,255,0.07) !important;
    border-radius: .625rem !important; box-shadow: 0 8px 32px rgba(0,0,0,0.5) !important;
    padding: .25rem !important; min-width: 140px !important;
  }
  .tt-dropdown-item {
    display: flex; align-items: center; gap: .5rem; padding: .45rem .625rem;
    font-size: .8rem; color: #93B4D4 !important; border-radius: .375rem;
    cursor: pointer; transition: background .16s, color .16s !important; outline: none;
    font-family: 'Outfit', system-ui, sans-serif;
  }
  .tt-dropdown-item:hover, .tt-dropdown-item:focus { background: rgba(34,211,238,0.10) !important; color: #22D3EE !important; }
  .tt-dropdown-item--danger:hover, .tt-dropdown-item--danger:focus { background: rgba(251,113,133,0.10) !important; color: #FB7185 !important; }
  .tt-dropdown-icon { width: .8rem; height: .8rem; flex-shrink: 0; }

  /* Empty */
  .tt-empty { padding: 3rem 1rem; text-align: center; color: #4D6E90; font-size: .875rem; }
  .tt-empty-icon {
    width: 2.5rem; height: 2.5rem; border-radius: .75rem;
    background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.055);
    display: flex; align-items: center; justify-content: center; margin: 0 auto .75rem;
  }
  .tt-empty-icon svg { width: 1.1rem; height: 1.1rem; color: #4D6E90; }

  /* Loading skeleton */
  .tt-loading { padding: 2rem 1.5rem; display: flex; flex-direction: column; gap: .875rem; }
  .tt-skeleton-row { display: flex; align-items: center; gap: .875rem; }
  .tt-sk { border-radius: .375rem; background: linear-gradient(90deg,#21262d 25%,#30363d 50%,#21262d 75%); background-size: 200% 100%; animation: tt-shimmer 1.5s infinite; }
  @keyframes tt-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .tt-sk-icon { width: 2rem; height: 2rem; border-radius: .5rem; flex-shrink: 0; }
  .tt-sk-lg   { height: .875rem; flex: 2; max-width: 160px; }
  .tt-sk-md   { height: .875rem; flex: 1.5; max-width: 110px; }
  .tt-sk-sm   { height: .875rem; flex: 1; max-width: 80px; }
  .tt-sk-pill { height: 1.5rem; width: 4.5rem; border-radius: 9999px; }

  /* Responsive */
  @media (max-width: 639px)  { .tt-hide-sm { display: none !important; } }
  @media (max-width: 767px)  { .tt-hide-md { display: none !important; } }
  @media (max-width: 1023px) { .tt-hide-lg { display: none !important; } }
`;

export default function TransactionTable({
  transactions = [],
  isLoading,
  onView,
  onRetry,
  onCancel,
  isQRCode = true,
  mixedTypes = false,
}) {
  let t = null;
  try {
    const langContext = useLanguage();
    t = langContext?.t;
  } catch (e) { }

  const tr = (key) => {
    if (t) return t(key);
    const fallbacks = {
      transaction: "Transaction", reseller: "Reseller", merchant: "Merchant",
      provider: "Provider", method: "Method", amount: "Amount", status: "Status",
      date: "Date", transactions: "No transactions found", ref: "Ref", direct: "Direct",
      viewDetails: "View Details", retry: "Retry", cancel: "Cancel",
      deposit: "Deposit", withdrawal: "Withdrawal",
    };
    return fallbacks[key] || key;
  };

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  if (isLoading) {
    return (
      <>
        <style>{TT_CSS}</style>
        <div className="tt-root">
          <div className="tt-loading">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="tt-skeleton-row" style={{ animationDelay: `${i * 60}ms`, opacity: 0, animation: `tt-shimmer .4s ${i * 60}ms forwards` }}>
                <div className="tt-sk tt-sk-icon" />
                <div className="tt-sk tt-sk-lg" />
                <div className="tt-sk tt-sk-md" />
                <div className="tt-sk tt-sk-sm" />
                <div className="tt-sk tt-sk-pill" />
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{TT_CSS}</style>
      <div className="tt-root">
        <div className="tt-scroll-wrap">
          <table className="tt-table">
            <thead>
              <tr className="tt-head-row">
                <th className="tt-th">{tr("transaction")}</th>
                <th className="tt-th tt-hide-md">{tr("reseller")}</th>
                <th className="tt-th">{tr("merchant")}</th>
                <th className="tt-th tt-hide-lg">{tr("provider")}</th>
                <th className="tt-th tt-th--right">{tr("netAmount" ?? "Net Amount")}</th>
                <th className="tt-th tt-th--right">{tr("totalFee" ?? "Total Fee")}</th>
                <th className="tt-th">{tr("status")}</th>
                <th className="tt-th tt-hide-sm">{tr("date")}</th>
                <th className="tt-th tt-th--action" />
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="tt-empty">
                    <div className="tt-empty-icon"><ArrowDownToLine /></div>
                    {tr("transactions")}
                  </td>
                </tr>
              ) : (
                transactions.map((tx, index) => {
                  const cfg = getStatusConfig(t);
                  const statusKey = (tx.status || "").toLowerCase();
                  const status = cfg[statusKey] || cfg.pending;
                  const StatusIcon = status.icon;
                  const isWithdrawal = tx.type === "WITHDRAWAL";
                  const displayId = isWithdrawal
                    ? (tx.transaction_id || tx.id)
                    : (isQRCode ? tx.payment_id : (tx.transaction_id || tx.id));

                  return (
                    <motion.tr
                      key={tx.id}
                      className="tt-body-row"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: Math.min(index * 0.018, 0.3) }}
                      onClick={() => onView?.(tx)}
                    >
                      {/* ID */}
                      <td className="tt-td tt-first-td">
                        <div className="tt-id-cell">
                          <div className={`tt-type-icon ${isWithdrawal ? "tt-type-icon--wd" : "tt-type-icon--dep"}`}>
                            {isWithdrawal ? <ArrowUpFromLine /> : <ArrowDownToLine />}
                          </div>
                          <div>
                            <div className="tt-id-row">
                              <span className="tt-id-mono">{(displayId?.slice(0, 14) || displayId) || "—"}</span>
                              <button className="tt-copy-btn" onClick={(e) => { e.stopPropagation(); copyToClipboard(displayId); }}>
                                <Copy />
                              </button>
                            </div>
                            {mixedTypes && (
                              <span className="tt-sub-text">
                                {isWithdrawal ? tr("withdrawal") : tr("deposit")}
                              </span>
                            )}
                            {!mixedTypes && isQRCode && tx.reference && (
                              <span className="tt-sub-text">{tr("ref")}: {tx.reference}</span>
                            )}
                            {!mixedTypes && !isQRCode && tx.merchant_ref && (
                              <span className="tt-sub-text">{tr("ref")}: {tx.merchant_ref}</span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Reseller */}
                      <td className="tt-td tt-hide-md">
                        {tx.reseller?.name
                          ? <span className="tt-reseller-name">{tx.reseller.name}</span>
                          : <span className="tt-direct-label">{tr("direct")}</span>}
                      </td>

                      {/* Merchant */}
                      <td className="tt-td">
                        <span className="tt-merchant-name">{tx.merchant?.name || "—"}</span>
                      </td>

                      {/* Provider */}
                      <td className="tt-td tt-hide-lg">
                        <span className="tt-provider-tag">
                          {isWithdrawal
                            ? (tx.client_bank_name || "N/A")
                            : (isQRCode ? (tx.client_bank_name || "N/A") : (tx.provider_code || "N/A"))}
                        </span>
                      </td>

                      {/* Amount */}
                      <td className="tt-td" style={{ textAlign: "right" }}>
                        <span className={isWithdrawal ? "tt-amount-wd" : "tt-amount-dep"}>
                          {isWithdrawal ? "-" : "+"}฿{(tx.net_amount ?? 0).toLocaleString()}
                        </span>
                      </td>

                      {/* Total Fee */}
                      <td className="tt-td" style={{ textAlign: "right" }}>
                        <span className="tt-amount-wd">
                          ฿{(tx.total_fee ?? 0).toLocaleString()}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="tt-td">
                        <span className="tt-status-badge" style={{
                          background: status.darkColor,
                          borderColor: status.darkBorder,
                          color: status.darkText,
                        }}>
                          <StatusIcon className={`tt-status-icon ${statusKey === "processing" ? "animate-spin" : ""}`} />
                          {status.label}
                        </span>
                      </td>

                      {/* Date */}
                      <td className="tt-td tt-hide-sm">
                        <div className="tt-date">
                          <span className="tt-date-main">
                            {(tx.created_date || tx.createdAt)
                              ? format(new Date(tx.created_date || tx.createdAt), "MMM d, yyyy")
                              : "N/A"}
                          </span>
                          <span className="tt-date-time">
                            {(tx.created_date || tx.createdAt)
                              ? format(new Date(tx.created_date || tx.createdAt), "HH:mm:ss")
                              : ""}
                          </span>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="tt-td">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="tt-action-btn" onClick={e => e.stopPropagation()}>
                              <MoreHorizontal />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="tt-dropdown-content">
                            <DropdownMenuItem className="tt-dropdown-item" onClick={() => onView?.(tx)}>
                              <Eye className="tt-dropdown-icon" />
                              {tr("viewDetails")}
                            </DropdownMenuItem>
                            {(tx.status === "failed" || tx.status === "expired") && (
                              <DropdownMenuItem className="tt-dropdown-item" onClick={() => onRetry?.(tx)}>
                                <RefreshCw className="tt-dropdown-icon" />
                                {tr("retry")}
                              </DropdownMenuItem>
                            )}
                            {tx.status === "pending" && (
                              <DropdownMenuItem className="tt-dropdown-item tt-dropdown-item--danger" onClick={() => onCancel?.(tx)}>
                                <XCircle className="tt-dropdown-icon" />
                                {tr("cancel")}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}